import React, { useCallback, useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap-5';
import GenericList from './generic_list';
import { get } from 'helpers/api';
import { debounce } from 'lodash';
import qs from 'qs';

interface User {
  id: number;
  name: string;
}

interface UsersListProps {
  companyId?: number;
  selectedUserIds: number[];
  onAddUser?: (id: number) => void;
  onRemoveUser?: (id: number) => void;
}

export default function UserLists({
  companyId,
  selectedUserIds,
  onAddUser,
  onRemoveUser
}: UsersListProps) {
  return (
    <Row>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">All Available Users</strong>
        </div>
        <UserList companyId={companyId} excludeUserIds={selectedUserIds || []} onAddUser={onAddUser}/>
      </Col>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">Users with Access</strong>
        </div>
        <UserList companyId={companyId} includeUserIds={selectedUserIds || []} onRemoveUser={onRemoveUser}/>
      </Col>
    </Row>
  );
}

interface UserListProps {
  companyId?: number;
  includeUserIds?: number[];
  excludeUserIds?: number[];
  onAddUser?: (id: number) => void;
  onRemoveUser?: (id: number) => void;
}
function UserList({
  companyId,
  includeUserIds,
  excludeUserIds,
  onAddUser,
  onRemoveUser
}: UserListProps) {
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);

  const searchUsers = useCallback(
    debounce((_searchText: string, _companyId?: number, _includeUserIds?: number[], _excludeUserIds?: number[]) => {
      const params: { limit?: number, [key: string]: any } = { limit: 15 };

      // When specifying the user ids, we do not need to apply a limit
      if (_includeUserIds && _includeUserIds.length > 0) params.limit = undefined;

      if (_searchText) params['q[first_name_or_last_name_or_login_credential_email_cont]'] = _searchText;
      if (_companyId) params['company_id'] = _companyId;
      let queryString = qs.stringify(params);

      if (_includeUserIds) _includeUserIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });

      if (_excludeUserIds) _excludeUserIds.forEach((id: number) => {
        queryString += `&q[id_not_in][]=${id}`;
      });

      get(`/users/search?${queryString}`).then((data: { users: User[] }) => {
        setUsers(data.users);
        setLoading(false);
      });
    }, 400),
    []
  );

  useEffect(() => {
    if (onRemoveUser && !includeUserIds?.length) {
      setUsers([]);
      return;
    }

    setLoading(true);
    searchUsers(searchText, companyId, includeUserIds, excludeUserIds);
  }, [searchText, companyId, includeUserIds, excludeUserIds]);

  const emptyText = () => {
    if (loading) return 'Loading...';

    if (onRemoveUser && includeUserIds?.length === 0) return 'Start by adding users';

    return 'No Users Found';
  };

  return (
    <GenericList
      emptyText={emptyText()}
      items={users}
      loading={loading}
      renderItemLabel={({ name }) => <div>{name}</div>}
      searchText={searchText}
      onAddItem={onAddUser}
      onRemoveItem={onRemoveUser}
      onSetSearchText={setSearchText}
    />
  );
}
